/**
 * CrewDocumentManager - Document Management UI for Crew
 * 
 * EF-CREW-DOCUMENT-MANAGEMENT-V1: Admin UI for managing crew documents.
 * 
 * Features:
 * - View documents by category (tabs)
 * - Upload new documents
 * - View/download documents
 * - OCR text viewing
 * - Document verification workflow
 * - Expiry tracking
 * - Onboarding status summary
 */
import { useState, useEffect, useCallback } from "react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import api from "../../lib/api";

// UI Components
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import { Progress } from "../ui/progress";
import { Separator } from "../ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

// Icons
import {
  FileText,
  Upload,
  Download,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  MoreVertical,
  Loader2,
  Calendar,
  FileSearch,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  CreditCard,
  MapPin,
  Car,
  Briefcase,
  User,
  FileWarning,
  CalendarDays,
  FolderOpen,
  Send,
  Camera,
  Pencil,
  Crop,
  ImageIcon,
  Tag,
  AlertOctagon,
} from "lucide-react";

// EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: Document Sharing Component
import SendDocumentsDialog from "./SendDocumentsDialog";

// EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Profile Photo Components
import { ProfilePhotoCropModal } from "./ProfilePhotoCropModal";

// EF-ENHANCE-DOCUMENT-PREVIEW-UX-ADMIN-AND-CREW-V1: Shared Preview Modal
import { DocumentPreviewModal } from "../shared/DocumentPreviewModal";
import { DocumentImageEditorModal } from "./DocumentImageEditorModal";

// Document status badge
const DocumentStatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    verified: "bg-green-500/20 text-green-400 border-green-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
    expired: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };
  
  const icons = {
    pending: <Clock className="h-3 w-3" />,
    verified: <CheckCircle className="h-3 w-3" />,
    rejected: <XCircle className="h-3 w-3" />,
    expired: <AlertTriangle className="h-3 w-3" />,
  };
  
  return (
    <Badge variant="outline" className={cn("text-xs gap-1", styles[status] || styles.pending)}>
      {icons[status]}
      {status}
    </Badge>
  );
};

// Category icon map
const CategoryIcon = ({ category, className = "h-4 w-4" }) => {
  const icons = {
    identity: <User className={className} />,
    compliance: <ShieldCheck className={className} />,
    experience: <Briefcase className={className} />,
    address: <MapPin className={className} />,
    license: <Car className={className} />,
    other: <FileText className={className} />,
  };
  return icons[category] || icons.other;
};

// Onboarding status badge
const OnboardingStatusBadge = ({ status }) => {
  const styles = {
    pending_documents: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    documents_uploaded: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    verification_required: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    verified: "bg-green-500/20 text-green-400 border-green-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
    inactive: "bg-gray-600/20 text-gray-500 border-gray-600/30",
  };
  
  const icons = {
    pending_documents: <FolderOpen className="h-3 w-3" />,
    documents_uploaded: <FileText className="h-3 w-3" />,
    verification_required: <ShieldQuestion className="h-3 w-3" />,
    verified: <ShieldCheck className="h-3 w-3" />,
    rejected: <ShieldAlert className="h-3 w-3" />,
    inactive: <XCircle className="h-3 w-3" />,
  };
  
  const labels = {
    pending_documents: "Pending Documents",
    documents_uploaded: "Awaiting Review",
    verification_required: "Verification Required",
    verified: "Verified",
    rejected: "Rejected",
    inactive: "Inactive",
  };
  
  return (
    <Badge variant="outline" className={cn("text-xs gap-1", styles[status] || styles.pending_documents)}>
      {icons[status]}
      {labels[status] || status}
    </Badge>
  );
};

// Document upload dialog
const UploadDocumentDialog = ({ isOpen, onClose, staffId, documentTypes, onSuccess }) => {
  const [selectedType, setSelectedType] = useState("");
  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  
  const handleUpload = async () => {
    if (!file || !selectedType) {
      toast.error("Please select a document type and file");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type_id", selectedType);
      if (issueDate) formData.append("issue_date", issueDate);
      if (expiryDate) formData.append("expiry_date", expiryDate);
      if (notes) formData.append("notes", notes);
      
      await api.post(`/staff/${staffId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      toast.success("Document uploaded successfully");
      onSuccess?.();
      onClose();
      
      // Reset form
      setSelectedType("");
      setFile(null);
      setIssueDate("");
      setExpiryDate("");
      setNotes("");
      
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error(err.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };
  
  // Get document type info for expiry requirement
  const selectedDocType = documentTypes.find(t => t.id === selectedType);
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Upload Document
          </DialogTitle>
          <DialogDescription>
            Upload a new document for this crew member
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Document Type */}
          <div className="space-y-2">
            <Label>Document Type *</Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger data-testid="doc-type-select">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="flex items-center gap-2">
                      <CategoryIcon category={type.category} className="h-4 w-4" />
                      {type.name}
                      {type.required_for_onboarding && (
                        <Badge variant="outline" className="text-[10px] ml-1">Required</Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* File Input */}
          <div className="space-y-2">
            <Label>File *</Label>
            <Input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0])}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              data-testid="doc-file-input"
            />
            <p className="text-xs text-muted-foreground">
              Accepted: PDF, JPG, PNG, DOC, DOCX (max 10MB)
            </p>
          </div>
          
          {/* Issue Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Issue Date</Label>
              <Input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </div>
            
            {/* Expiry Date */}
            <div className="space-y-2">
              <Label>
                Expiry Date
                {selectedDocType?.requires_expiry && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this document..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={uploading || !file || !selectedType}>
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Portrait Detection Banner
const PortraitDetectionBanner = ({ 
  document, 
  staffId,
  onDetectPortrait,
  detecting,
  detectionResult,
  onOpenCropModal 
}) => {
  // EF-CREW-PROFILE-PHOTO-CROP-V1: Accept file_url OR document.id for checking
  if ((!document?.file_url && !document?.id) || detecting === undefined) return null;
  
  // EF-FIX-DOCUMENT-PREVIEW-MIME-TYPE-MAPPING-V1: Use includes() for MIME type check
  const mimeType = (document.mime_type || "").toLowerCase();
  const isImage = mimeType.includes("image") || document.original_filename?.match(/\.(jpg|jpeg|png|gif|webp|heic|heif)$/i);
  
  if (!isImage) return null;
  
  if (detecting) {
    return (
      <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-3">
        <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
        <span className="text-sm text-blue-300">Detecting portrait...</span>
      </div>
    );
  }
  
  if (detectionResult) {
    if (detectionResult.faces_count === 0) {
      return null; // No faces detected, don't show anything
    }
    
    if (detectionResult.faces_count === 1) {
      return (
        <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg mb-3">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-emerald-400" />
            <span className="text-sm text-emerald-300">Detected portrait — Use as profile photo</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            onClick={onOpenCropModal}
            data-testid="use-as-profile-photo-btn"
          >
            <ImageIcon className="h-3.5 w-3.5 mr-1.5" />
            Use as Profile Photo
          </Button>
        </div>
      );
    }
    
    // Multiple faces detected
    return (
      <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <span className="text-sm text-amber-300">
            Multiple faces detected ({detectionResult.faces_count}) — select the correct portrait
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          onClick={onOpenCropModal}
          data-testid="select-portrait-btn"
        >
          <ImageIcon className="h-3.5 w-3.5 mr-1.5" />
          Select Portrait
        </Button>
      </div>
    );
  }
  
  // Not detected yet - show detect button
  return (
    <div className="flex items-center justify-between p-3 bg-surface-elevated/30 border border-white/10 rounded-lg mb-3">
      <div className="flex items-center gap-2">
        <Camera className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Detect portrait in this document</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onDetectPortrait}
        data-testid="detect-portrait-btn"
      >
        <Eye className="h-3.5 w-3.5 mr-1.5" />
        Detect Portrait
      </Button>
    </div>
  );
};

// OCR Viewer Dialog with Portrait Detection
const OCRViewerDialog = ({ isOpen, onClose, document, onUpdate, staffId }) => {
  const [ocrText, setOcrText] = useState("");
  const [editedFields, setEditedFields] = useState({});
  const [saving, setSaving] = useState(false);
  
  // EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Portrait detection state
  const [detectingPortrait, setDetectingPortrait] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  
  useEffect(() => {
    if (document?.ocr_data) {
      setOcrText(document.ocr_data.raw_text || "");
      setEditedFields({
        last_name: document.ocr_data.last_name || "",
        first_name: document.ocr_data.first_name || "",
        date_of_birth: document.ocr_data.date_of_birth || "",
        nationality: document.ocr_data.nationality || "",
        address: document.ocr_data.address || "",
        document_number: document.ocr_data.document_number || "",
        vitale_code: document.ocr_data.vitale_code || "",
      });
    }
    // Reset detection result when document changes
    setDetectionResult(null);
  }, [document]);
  
  // EF-CREW-PROFILE-PHOTO-CROP-V1: Detect portrait in document
  // Updated to use crew-documents endpoint instead of document-assets
  const handleDetectPortrait = async () => {
    // Use document.id directly (asset_id now equals document.id)
    const docId = document?.asset_id || document?.id;
    if (!docId) {
      toast.error("Document ID not found");
      return;
    }
    
    setDetectingPortrait(true);
    try {
      // EF-CREW-PROFILE-PHOTO-CROP-V1: Use new crew-documents endpoint
      const response = await api.post(`/crew-documents/${docId}/detect-portrait`);
      setDetectionResult(response.data.detection_result || response.data);
      
      const facesCount = response.data.faces_count || response.data.detection_result?.faces_count || 0;
      if (facesCount > 0) {
        toast.success(`Detected ${facesCount} face(s) in document`);
      } else {
        toast.info("No portrait detected in this document");
      }
    } catch (err) {
      console.error("Portrait detection error:", err);
      toast.error("Failed to detect portrait");
    } finally {
      setDetectingPortrait(false);
    }
  };
  
  // Handle profile photo saved
  const handleProfilePhotoSaved = (data) => {
    toast.success("Profile photo saved successfully");
    onUpdate?.();
  };
  
  const handleSaveOCR = async () => {
    setSaving(true);
    try {
      await api.patch(`/staff/${document.staff_id}/documents/${document.id}/ocr`, editedFields);
      toast.success("OCR data updated");
      onUpdate?.();
      onClose();
    } catch (err) {
      toast.error("Failed to update OCR data");
    } finally {
      setSaving(false);
    }
  };
  
  if (!document) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5 text-primary" />
            OCR Extracted Data
          </DialogTitle>
          <DialogDescription>
            Review and correct OCR extracted fields before verification
          </DialogDescription>
        </DialogHeader>
        
        {/* EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Portrait Detection Banner */}
        <PortraitDetectionBanner
          document={document}
          staffId={staffId}
          onDetectPortrait={handleDetectPortrait}
          detecting={detectingPortrait}
          detectionResult={detectionResult}
          onOpenCropModal={() => setCropModalOpen(true)}
        />
        
        <div className="grid grid-cols-2 gap-4 flex-1 overflow-hidden">
          {/* Raw OCR Text */}
          <div className="space-y-2">
            <Label>Raw OCR Text</Label>
            <ScrollArea className="h-[300px] border rounded-md p-3 bg-muted/30">
              <pre className="text-xs whitespace-pre-wrap font-mono">
                {ocrText || "No OCR text available"}
              </pre>
            </ScrollArea>
          </div>
          
          {/* Extracted Fields */}
          <div className="space-y-3">
            <Label>Extracted Fields</Label>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3 pr-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Last Name</Label>
                    <Input
                      value={editedFields.last_name}
                      onChange={(e) => setEditedFields(prev => ({ ...prev, last_name: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">First Name</Label>
                    <Input
                      value={editedFields.first_name}
                      onChange={(e) => setEditedFields(prev => ({ ...prev, first_name: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Date of Birth</Label>
                    <Input
                      value={editedFields.date_of_birth}
                      onChange={(e) => setEditedFields(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nationality</Label>
                    <Input
                      value={editedFields.nationality}
                      onChange={(e) => setEditedFields(prev => ({ ...prev, nationality: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Document Number</Label>
                  <Input
                    value={editedFields.document_number}
                    onChange={(e) => setEditedFields(prev => ({ ...prev, document_number: e.target.value }))}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Vitale Code</Label>
                  <Input
                    value={editedFields.vitale_code}
                    onChange={(e) => setEditedFields(prev => ({ ...prev, vitale_code: e.target.value }))}
                    className="h-8"
                    placeholder="1 94 10 06 088 414 48"
                  />
                </div>
                <div>
                  <Label className="text-xs">Address</Label>
                  <Textarea
                    value={editedFields.address}
                    onChange={(e) => setEditedFields(prev => ({ ...prev, address: e.target.value }))}
                    className="h-16 text-sm"
                  />
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSaveOCR} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Profile Photo Crop Modal */}
      {detectionResult && (
        <ProfilePhotoCropModal
          open={cropModalOpen}
          onOpenChange={setCropModalOpen}
          imageUrl={document?.file_url}
          staffId={staffId}
          assetId={document?.asset_id}
          suggestedCrop={detectionResult?.suggested_crop}
          allFaces={detectionResult?.all_faces || []}
          onSaveComplete={handleProfilePhotoSaved}
        />
      )}
    </Dialog>
  );
};

// Main Component
export function CrewDocumentManager({ staffId, staffName }) {
  const [loading, setLoading] = useState(true);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [historicalDocuments, setHistoricalDocuments] = useState([]);
  const [onboardingSummary, setOnboardingSummary] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [showHistory, setShowHistory] = useState(false);
  
  // Dialogs
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [ocrViewerOpen, setOcrViewerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [verifyAction, setVerifyAction] = useState(null);
  // ONB-FIX-022: Reassign document type
  const [reassignDoc, setReassignDoc] = useState(null);
  const [reassignTargetTypeId, setReassignTargetTypeId] = useState("");
  const [reassignSaving, setReassignSaving] = useState(false);
  // ONB-FIX-026: Manual expiry date edit
  const [expiryDoc, setExpiryDoc] = useState(null);
  const [expiryValue, setExpiryValue] = useState("");
  const [expirySaving, setExpirySaving] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  // EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: Send documents dialog
  const [sendDocsOpen, setSendDocsOpen] = useState(false);
  // EF-FIX-CREW-ADMIN-DOCUMENT-VIEWER-UNKNOWN-TYPE-EXACT-SCREEN-V1: Preview dialog
  const [previewDoc, setPreviewDoc] = useState(null);

  // EVENTSFLO-CREW-DOCUMENT-RENAME-DISPLAY-FILENAME-V1
  // Rename / display filename dialog state.
  const [renameDoc, setRenameDoc] = useState(null); // the document being renamed
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameSuggested, setRenameSuggested] = useState("");

  const openRenameDialog = useCallback(async (doc) => {
    setRenameDoc(doc);
    // Seed with the document's current display name; fall back to original.
    const seed = doc.display_filename || doc.original_filename || "";
    setRenameValue(seed);
    setRenameSuggested("");
    try {
      const { data } = await api.get(
        `/staff/${staffId}/documents/${doc.id}/default-display-filename`
      );
      const suggestion = data?.default_display_filename;
      if (suggestion) {
        setRenameSuggested(suggestion);
        // Only auto-fill the suggestion when the current name looks like a
        // raw camera capture (the seed equals the original_filename) — never
        // overwrite an admin-curated value.
        if (!doc.display_filename) setRenameValue(suggestion);
      }
    } catch (_) {
      // Soft-fail — admin can still type a name manually.
    }
  }, [staffId]);

  // EVENTSFLO-CREW-DOCUMENT-IMAGE-CROP-ROTATE-CLEAN-VERSION-V1
  // Document image editor (crop + rotate). Image MIMEs only — PDFs hide the
  // menu item entirely.
  const [editorDoc, setEditorDoc] = useState(null);
  const _IMAGE_EDITABLE_MIMES = new Set([
    "image/jpeg", "image/jpg", "image/png", "image/webp",
  ]);
  const isImageDoc = (doc) => doc && _IMAGE_EDITABLE_MIMES.has((doc.mime_type || "").toLowerCase());

  const submitRename = useCallback(async () => {
    if (!renameDoc) return;
    const v = (renameValue || "").trim();
    if (!v) {
      toast.error("Filename cannot be empty");
      return;
    }
    setRenameSaving(true);
    try {
      const { data } = await api.post(
        `/staff/${staffId}/documents/${renameDoc.id}/rename`,
        { display_filename: v }
      );
      toast.success(`Renamed to ${data.display_filename}`);
      setRenameDoc(null);
      setRenameValue("");
      setRenameSuggested("");
      await loadData();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Rename failed";
      toast.error(typeof msg === "string" ? msg : "Rename failed");
    } finally {
      setRenameSaving(false);
    }
  }, [renameDoc, renameValue, staffId]);
  
  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [typesRes, docsRes, summaryRes] = await Promise.all([
        api.get("/document-types"),
        api.get(`/staff/${staffId}/documents/with-history`),
        api.get(`/staff/${staffId}/onboarding-summary`),
      ]);
      
      setDocumentTypes(typesRes.data || []);
      setDocuments(docsRes.data?.current_documents || []);
      setHistoricalDocuments(docsRes.data?.historical_documents || []);
      setOnboardingSummary(summaryRes.data);
      
    } catch (err) {
      console.error("Failed to load document data:", err);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [staffId]);
  
  useEffect(() => {
    if (staffId) {
      loadData();
    }
  }, [staffId, loadData]);
  
  // Get displayed documents (current or historical based on toggle)
  const displayedDocuments = showHistory ? historicalDocuments : documents;
  
  // Filter documents by category
  const filteredDocuments = activeCategory === "all"
    ? displayedDocuments
    : displayedDocuments.filter(d => {
        const docType = documentTypes.find(t => t.id === d.document_type_id);
        return docType?.category === activeCategory;
      });
  
  // Handle document download
  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/staff/${staffId}/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download document");
    }
  };
  
  // Handle delete
  const handleDelete = async (doc) => {
    try {
      await api.delete(`/staff/${staffId}/documents/${doc.id}`);
      toast.success("Document deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete document");
    }
    setConfirmDelete(null);
  };

  // ONB-FIX-026: Manual expiry-date edit
  const openExpiryEdit = (doc) => {
    setExpiryDoc(doc);
    // Backend stores YYYY-MM-DD; <Input type="date"> needs the same.
    const raw = doc.expiry_date || "";
    setExpiryValue(typeof raw === "string" ? raw.slice(0, 10) : "");
  };
  const handleSaveExpiry = async ({ clear = false } = {}) => {
    if (!expiryDoc) return;
    setExpirySaving(true);
    try {
      const payload = clear
        ? { expiry_date: null }
        : { expiry_date: expiryValue || null };
      await api.patch(
        `/staff/${staffId}/documents/${expiryDoc.id}`,
        payload
      );
      toast.success(clear ? "Expiry date cleared" : "Expiry date saved");
      setExpiryDoc(null);
      setExpiryValue("");
      await loadData();
    } catch (err) {
      console.error("Save expiry failed:", err);
      toast.error(
        err?.response?.data?.detail || err?.message || "Failed to save expiry date"
      );
    } finally {
      setExpirySaving(false);
    }
  };

  // ONB-FIX-022: Reassign a document to a different document type
  const openReassign = (doc) => {
    setReassignDoc(doc);
    setReassignTargetTypeId(doc.document_type_id || "");
  };
  const handleReassign = async () => {
    if (!reassignDoc || !reassignTargetTypeId) return;
    if (reassignTargetTypeId === reassignDoc.document_type_id) {
      toast.info("Document is already assigned to this type");
      setReassignDoc(null);
      return;
    }
    setReassignSaving(true);
    try {
      await api.patch(
        `/staff/${staffId}/documents/${reassignDoc.id}`,
        { document_type_id: reassignTargetTypeId }
      );
      const newType = documentTypes.find((t) => t.id === reassignTargetTypeId);
      toast.success(`Document reassigned to ${newType?.name || "new type"}`);
      setReassignDoc(null);
      setReassignTargetTypeId("");
      await loadData();
    } catch (err) {
      console.error("Reassign failed:", err);
      toast.error(
        err?.response?.data?.detail || err?.message || "Failed to reassign document"
      );
    } finally {
      setReassignSaving(false);
    }
  };
  
  // Handle verification
  const handleVerify = async (doc, action, reason = "") => {
    try {
      await api.post(`/staff/${staffId}/documents/${doc.id}/verify`, {
        action,
        rejection_reason: reason || undefined,
        apply_ocr_to_profile: false,
      });
      
      toast.success(action === "verify" ? "Document verified" : "Document rejected");
      
      // EF-FIX-DOC-PREVIEW-VERIFY-STATUS-SORT-V1: Ensure data refresh after verification
      await loadData();
    } catch (err) {
      console.error("Verification failed:", err);
      toast.error(`Failed to ${action} document`);
    }
    setVerifyAction(null);
  };
  
  // Get document type info
  const getDocumentType = (docTypeId) => documentTypes.find(t => t.id === docTypeId);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="crew-document-manager">
      {/* Header with Onboarding Summary */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage identity and compliance documents for {staffName}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: Send Documents button */}
          {documents.length > 0 && (
            <Button 
              variant="outline" 
              onClick={() => setSendDocsOpen(true)} 
              data-testid="send-docs-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Documents
            </Button>
          )}
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-doc-btn">
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>
      
      {/* Onboarding Progress Card */}
      {onboardingSummary && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-medium">Onboarding Status</CardTitle>
                <CardDescription className="text-xs">
                  {onboardingSummary.total_verified} of {onboardingSummary.total_required} required documents verified
                </CardDescription>
              </div>
              <OnboardingStatusBadge status={onboardingSummary.onboarding_status} />
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={onboardingSummary.completion_percentage} className="h-2" />
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>{Math.round(onboardingSummary.completion_percentage)}% complete</span>
              {onboardingSummary.can_work ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30 text-[10px]">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Ready to Work
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px]">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Documents Required
                </Badge>
              )}
            </div>
            
            {/* Quick stats */}
            <div className="grid grid-cols-5 gap-4 mt-4 pt-4 border-t">
              <div className="text-center">
                <div className="text-lg font-semibold">{onboardingSummary.total_uploaded}</div>
                <div className="text-xs text-muted-foreground">Uploaded</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-green-400">{onboardingSummary.total_verified}</div>
                <div className="text-xs text-muted-foreground">Verified</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-amber-400">{onboardingSummary.total_pending}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-red-400">{onboardingSummary.total_rejected}</div>
                <div className="text-xs text-muted-foreground">Rejected</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-400">{onboardingSummary.total_expired}</div>
                <div className="text-xs text-muted-foreground">Expired</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList>
          <TabsTrigger value="all" className="gap-1">
            <FileText className="h-4 w-4" />
            All
          </TabsTrigger>
          <TabsTrigger value="identity" className="gap-1">
            <User className="h-4 w-4" />
            Identity
          </TabsTrigger>
          <TabsTrigger value="compliance" className="gap-1">
            <ShieldCheck className="h-4 w-4" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="address" className="gap-1">
            <MapPin className="h-4 w-4" />
            Address
          </TabsTrigger>
          <TabsTrigger value="license" className="gap-1">
            <Car className="h-4 w-4" />
            Licenses
          </TabsTrigger>
          <TabsTrigger value="experience" className="gap-1">
            <Briefcase className="h-4 w-4" />
            Experience
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeCategory} className="mt-4">
          {/* Documents Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No documents in this category
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocuments.map((doc) => {
                    const docType = getDocumentType(doc.document_type_id);
                    const isExpiringSoon = doc.expiry_date && new Date(doc.expiry_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    
                    // EF-FIX-CREW-ADMIN-DOCUMENT-VIEWER-UNKNOWN-TYPE-EXACT-SCREEN-V1:
                    // Get display name with fallback to filename or mime-based label
                    const mime = (doc.mime_type || "").toLowerCase();
                    let displayName = docType?.name;
                    if (!displayName) {
                      // Fallback: infer label from mime type
                      if (mime.includes("image")) displayName = "Image Document";
                      else if (mime.includes("pdf")) displayName = "PDF Document";
                      else displayName = doc.original_filename || "Document";
                    }
                    
                    return (
                      <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-md bg-primary/10">
                              <CategoryIcon category={docType?.category} className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                <span>{displayName}</span>
                                {/* EVENTSFLO-CREW-DOCUMENTS-FILE-MISSING-BADGE-AND-SAFE-VISIBILITY-V1:
                                    Red "File missing" badge when backing file is gone. */}
                                {doc.file_missing && (
                                  <Badge
                                    variant="destructive"
                                    data-testid={`file-missing-badge-${doc.id}`}
                                    title="File is missing from storage. Metadata remains for audit purposes."
                                    className="gap-1 text-[10px]"
                                  >
                                    <AlertOctagon className="h-3 w-3" />
                                    File missing
                                  </Badge>
                                )}
                              </div>
                              {/* EF-ENHANCE-DOCUMENT-PREVIEW-UX-ADMIN-AND-CREW-V1: Clickable filename
                                  EVENTSFLO-CREW-DOCUMENT-RENAME-DISPLAY-FILENAME-V1:
                                  Prefer admin-curated display_filename so renames are visible
                                  in the table. Falls back to the immutable original_filename.
                                  EVENTSFLO-CREW-DOCUMENTS-FILE-MISSING-BADGE-AND-SAFE-VISIBILITY-V1:
                                  When file is missing, disable preview click. */}
                              <button
                                onClick={() => !doc.file_missing && setPreviewDoc(doc)}
                                disabled={!!doc.file_missing}
                                className={
                                  doc.file_missing
                                    ? "text-xs text-muted-foreground line-through cursor-not-allowed text-left opacity-70"
                                    : "text-xs text-muted-foreground hover:underline hover:text-primary cursor-pointer text-left"
                                }
                                title={
                                  doc.file_missing
                                    ? "File is missing from storage. Metadata remains for audit purposes."
                                    : `Click to preview: ${doc.display_filename || doc.original_filename}`
                                }
                                data-testid={`preview-filename-${doc.id}`}
                              >
                                {doc.display_filename || doc.original_filename}
                              </button>
                            </div>
                          </div>
                        </TableCell>
                        
                        <TableCell>
                          <DocumentStatusBadge status={doc.status} />
                        </TableCell>
                        
                        <TableCell className="text-sm">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </TableCell>
                        
                        <TableCell>
                          {doc.expiry_date ? (
                            <div className={cn(
                              "text-sm",
                              isExpiringSoon && doc.status !== "expired" && "text-amber-400"
                            )}>
                              {new Date(doc.expiry_date).toLocaleDateString()}
                              {isExpiringSoon && doc.status !== "expired" && (
                                <CalendarDays className="h-3 w-3 inline ml-1" />
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">N/A</span>
                          )}
                        </TableCell>
                        
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* EF-FIX-CREW-ADMIN-DOCUMENT-VIEWER-UNKNOWN-TYPE-EXACT-SCREEN-V1: Add Preview
                                  EVENTSFLO-CREW-DOCUMENTS-FILE-MISSING-BADGE-AND-SAFE-VISIBILITY-V1:
                                  Disable Preview/Download when backing file is missing —
                                  action would 404. Rename/Delete/Archive stay enabled so
                                  operator can clean up the metadata row. */}
                              <DropdownMenuItem
                                onClick={() => !doc.file_missing && setPreviewDoc(doc)}
                                disabled={!!doc.file_missing}
                                data-testid={`preview-action-${doc.id}`}
                                title={doc.file_missing ? "File is missing from storage. Metadata remains for audit purposes." : undefined}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Preview
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => !doc.file_missing && handleDownload(doc)}
                                disabled={!!doc.file_missing}
                                data-testid={`download-action-${doc.id}`}
                                title={doc.file_missing ? "File is missing from storage. Metadata remains for audit purposes." : undefined}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </DropdownMenuItem>
                              {doc.ocr_processed && (
                                <DropdownMenuItem onClick={() => {
                                  setSelectedDocument(doc);
                                  setOcrViewerOpen(true);
                                }}>
                                  <FileSearch className="h-4 w-4 mr-2" />
                                  View OCR Data
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {doc.status === "pending" && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => setVerifyAction({ doc, action: "verify" })}
                                    className="text-green-400"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Verify
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => setVerifyAction({ doc, action: "reject" })}
                                    className="text-red-400"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Reject
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem
                                onClick={() => openExpiryEdit(doc)}
                                data-testid={`edit-expiry-${doc.id}`}
                              >
                                <CalendarDays className="h-4 w-4 mr-2" />
                                Edit Expiry Date
                              </DropdownMenuItem>
                              {/* EVENTSFLO-CREW-DOCUMENT-RENAME-DISPLAY-FILENAME-V1 */}
                              <DropdownMenuItem
                                onClick={() => openRenameDialog(doc)}
                                data-testid={`rename-document-${doc.id}`}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Rename file
                              </DropdownMenuItem>
                              {/* EVENTSFLO-CREW-DOCUMENT-IMAGE-CROP-ROTATE-CLEAN-VERSION-V1
                                  Image-only. PDFs and other non-image MIMEs
                                  hide this item entirely. */}
                              {isImageDoc(doc) && (
                                <DropdownMenuItem
                                  onClick={() => setEditorDoc(doc)}
                                  data-testid={`crop-rotate-document-${doc.id}`}
                                >
                                  <Crop className="h-4 w-4 mr-2" />
                                  Crop / rotate image
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => openReassign(doc)}
                                data-testid={`reassign-document-${doc.id}`}
                              >
                                <Tag className="h-4 w-4 mr-2" />
                                Change Type
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => setConfirmDelete(doc)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Required Documents Checklist */}
      {onboardingSummary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Required Documents Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {onboardingSummary.document_statuses
                .filter(s => s.required)
                .map((status) => (
                  <div
                    key={status.document_type_code}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded-md border",
                      status.status === "verified" && "border-green-500/30 bg-green-500/5",
                      status.status === "pending" && "border-amber-500/30 bg-amber-500/5",
                      status.status === "rejected" && "border-red-500/30 bg-red-500/5",
                      !status.uploaded && "border-muted bg-muted/20"
                    )}
                    data-testid={`required-doc-${status.document_type_code}`}
                    data-status={status.status || (status.uploaded ? "pending" : "missing")}
                  >
                    {status.status === "verified" ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : status.status === "rejected" ? (
                      <XCircle className="h-4 w-4 text-red-400" />
                    ) : status.uploaded ? (
                      <Clock className="h-4 w-4 text-amber-400" />
                    ) : (
                      <FileWarning className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="text-sm flex-1 truncate">{status.document_type_name}</span>
                    {/* ONB-FIX-029: Explicit status label so checklist tile and table row
                        unambiguously read the same truth (verified / pending / rejected / missing). */}
                    <span
                      className={cn(
                        "text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border",
                        status.status === "verified" && "border-green-500/30 text-green-400",
                        status.status === "pending" && "border-amber-500/30 text-amber-400",
                        status.status === "rejected" && "border-red-500/30 text-red-400",
                        !status.uploaded && "border-muted text-muted-foreground"
                      )}
                    >
                      {!status.uploaded
                        ? "Missing"
                        : status.status === "verified"
                          ? "Verified"
                          : status.status === "rejected"
                            ? "Rejected"
                            : "Pending"}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Upload Dialog */}
      <UploadDocumentDialog
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        staffId={staffId}
        documentTypes={documentTypes}
        onSuccess={loadData}
      />
      
      {/* OCR Viewer Dialog */}
      <OCRViewerDialog
        isOpen={ocrViewerOpen}
        onClose={() => {
          setOcrViewerOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onUpdate={loadData}
        staffId={staffId}
      />
      
      {/* Delete Confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{confirmDelete?.original_filename}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(confirmDelete)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Verify/Reject Dialog */}
      <VerificationDialog
        isOpen={!!verifyAction}
        action={verifyAction?.action}
        document={verifyAction?.doc}
        onClose={() => setVerifyAction(null)}
        onConfirm={(reason) => handleVerify(verifyAction.doc, verifyAction.action, reason)}
      />

      {/* ONB-FIX-026: Edit Expiry Date Dialog */}
      <Dialog
        open={!!expiryDoc}
        onOpenChange={(open) => {
          if (!open) {
            setExpiryDoc(null);
            setExpiryValue("");
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="edit-expiry-dialog">
          <DialogHeader>
            <DialogTitle>Edit Expiry Date</DialogTitle>
            <DialogDescription>
              Set or update the expiry date for &quot;{expiryDoc?.original_filename}&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs text-muted-foreground">Expiry Date</Label>
            <Input
              type="date"
              value={expiryValue}
              onChange={(e) => setExpiryValue(e.target.value)}
              className="bg-background"
              data-testid="edit-expiry-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Leave empty and click <span className="font-medium">Clear</span> to remove the expiry date.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => handleSaveExpiry({ clear: true })}
              disabled={expirySaving}
              data-testid="edit-expiry-clear"
            >
              Clear
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setExpiryDoc(null);
                setExpiryValue("");
              }}
              disabled={expirySaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSaveExpiry({ clear: false })}
              disabled={expirySaving || !expiryValue}
              data-testid="edit-expiry-save"
            >
              {expirySaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CalendarDays className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ONB-FIX-022: Reassign Document Type Dialog */}
      <Dialog
        open={!!reassignDoc}
        onOpenChange={(open) => {
          if (!open) {
            setReassignDoc(null);
            setReassignTargetTypeId("");
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="reassign-document-dialog">
          <DialogHeader>
            <DialogTitle>Change Document Type</DialogTitle>
            <DialogDescription>
              Reassign &quot;{reassignDoc?.original_filename}&quot; to a different document type.
              Required-document checklist items will update accordingly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs text-muted-foreground">New Document Type</Label>
            <Select value={reassignTargetTypeId} onValueChange={setReassignTargetTypeId}>
              <SelectTrigger data-testid="reassign-document-type-select">
                <SelectValue placeholder="Select document type..." />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.required_for_onboarding ? " (required)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReassignDoc(null);
                setReassignTargetTypeId("");
              }}
              disabled={reassignSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReassign}
              disabled={
                reassignSaving ||
                !reassignTargetTypeId ||
                reassignTargetTypeId === reassignDoc?.document_type_id
              }
              data-testid="reassign-document-confirm"
            >
              {reassignSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Tag className="h-4 w-4 mr-2" />
              )}
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: Send Documents Dialog */}
      <SendDocumentsDialog
        open={sendDocsOpen}
        onOpenChange={setSendDocsOpen}
        staffId={staffId}
        staffName={staffName}
        documents={documents}
        onSuccess={() => {
          loadData();
          toast.success("Documents shared successfully");
        }}
      />
      
      {/* EF-ENHANCE-DOCUMENT-PREVIEW-UX-ADMIN-AND-CREW-V1: Use shared Document Preview Modal */}
      <DocumentPreviewModal
        document={previewDoc}
        open={!!previewDoc}
        onOpenChange={(open) => !open && setPreviewDoc(null)}
        fallbackFileUrl={previewDoc ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/staff/${staffId}/documents/${previewDoc?.id}/download` : undefined}
      />

      {/* EVENTSFLO-CREW-DOCUMENT-RENAME-DISPLAY-FILENAME-V1
          Admin-only Rename file dialog. Updates `display_filename` only;
          physical file and original_filename are never modified. */}
      <Dialog
        open={!!renameDoc}
        onOpenChange={(open) => { if (!open && !renameSaving) { setRenameDoc(null); setRenameValue(""); setRenameSuggested(""); } }}
      >
        <DialogContent className="max-w-md" data-testid="rename-document-dialog">
          <DialogHeader>
            <DialogTitle>Rename file</DialogTitle>
            <DialogDescription className="text-xs">
              Only the display name changes. The original file on storage is
              untouched. Allowed: letters, numbers, spaces, dashes, dots and
              underscores. Extension is preserved automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Original filename</Label>
              <div
                className="text-xs font-mono truncate"
                data-testid="rename-original-filename"
              >
                {renameDoc?.original_filename || "—"}
              </div>
            </div>
            {renameSuggested && (
              <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2 py-1.5">
                <div className="text-xs truncate">
                  <span className="text-muted-foreground">Suggested:&nbsp;</span>
                  <span className="font-mono" data-testid="rename-suggested-default">
                    {renameSuggested}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setRenameValue(renameSuggested)}
                  disabled={renameSaving || renameValue === renameSuggested}
                  data-testid="rename-use-suggested-btn"
                >
                  Use
                </Button>
              </div>
            )}
            <div>
              <Label htmlFor="rename-input" className="text-xs">
                Display filename
              </Label>
              <Input
                id="rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="e.g. ALTEMIROV-Ilyas-National-ID-Front.jpg"
                disabled={renameSaving}
                maxLength={200}
                data-testid="rename-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => { setRenameDoc(null); setRenameValue(""); setRenameSuggested(""); }}
              disabled={renameSaving}
              data-testid="rename-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRename}
              disabled={renameSaving || !renameValue.trim()}
              data-testid="rename-save-btn"
            >
              {renameSaving ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
              ) : (
                <><Pencil className="h-4 w-4 mr-1" /> Save</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-CREW-DOCUMENT-IMAGE-CROP-ROTATE-CLEAN-VERSION-V1
          Admin-only image crop/rotate editor. Creates a NEW VERSION on save
          via POST /api/staff/{sid}/documents/{did}/image-edit; original is
          preserved in version history. */}
      <DocumentImageEditorModal
        open={!!editorDoc}
        onOpenChange={(v) => { if (!v) setEditorDoc(null); }}
        document={editorDoc}
        staffId={staffId}
        onSaveComplete={async () => { setEditorDoc(null); await loadData(); }}
      />
    </div>
  );
}

// Verification Dialog
function VerificationDialog({ isOpen, action, document, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [applyToProfile, setApplyToProfile] = useState(false);
  
  if (!isOpen) return null;
  
  const isVerify = action === "verify";
  
  return (
    <AlertDialog open={isOpen} onOpenChange={() => onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isVerify ? "Verify Document?" : "Reject Document?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isVerify 
              ? "This will mark the document as verified. The crew member's onboarding status will be updated."
              : "Please provide a reason for rejection. The crew member will need to upload a new document."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        {!isVerify && (
          <div className="space-y-2">
            <Label>Rejection Reason *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Document is blurry, expired, or invalid..."
              rows={3}
            />
          </div>
        )}
        
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(reason)}
            disabled={!isVerify && !reason.trim()}
            className={isVerify ? "bg-green-600 hover:bg-green-700" : "bg-destructive hover:bg-destructive/90"}
          >
            {isVerify ? "Verify" : "Reject"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default CrewDocumentManager;
